import React from 'react';
import { ethers } from 'ethers';
import { Transaction } from '../types';

interface StakingPanelProps {
  tokenBalance: string;
  stakedV1: string;
  stakedV2: string;
  cooldownV1: string;
  cooldownV2: string;
  amount: string;
  onAmountChange: (value: string) => void;
  onStakeV2: () => void;
  onUnstakeV1: () => void;
  onUnstakeV2: () => void;
  onSetMaxWallet: () => void;
  onSetMaxV1: () => void;
  onSetMaxV2: () => void;
  isStaking: boolean;
  isUnstaking: boolean;
  status: string;
  bothWalletsPresent: boolean;
  onOpenWalletSetup: () => void;
  tokenBalanceWei: ethers.BigNumber;
  stakedV1Wei: ethers.BigNumber;
  stakedV2Wei: ethers.BigNumber;
  transactions: Transaction[];
}

const StakingPanel: React.FC<StakingPanelProps> = ({
  tokenBalance,
  stakedV1,
  stakedV2,
  cooldownV1,
  cooldownV2,
  amount,
  onAmountChange,
  onStakeV2,
  onUnstakeV1,
  onUnstakeV2,
  onSetMaxWallet,
  onSetMaxV1,
  onSetMaxV2,
  isStaking,
  isUnstaking,
  status,
  bothWalletsPresent,
  onOpenWalletSetup,
  tokenBalanceWei,
  stakedV1Wei,
  stakedV2Wei,
  transactions
}) => {
  const anyBusy = isStaking || isUnstaking;
  const hasNoECYAnywhere = tokenBalanceWei.isZero() && stakedV1Wei.isZero() && stakedV2Wei.isZero();
  const hasV1Stake = stakedV1Wei.gt(ethers.constants.Zero);
  const canStakeV2 = !hasV1Stake && tokenBalanceWei.gt(ethers.constants.Zero);

  const formatNumber = (num: string) => {
    const parts = (num ?? '0').split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  const totalStaked = (parseFloat(stakedV1 || '0') + parseFloat(stakedV2 || '0')).toFixed(6);

  const totalXCH = transactions
    .filter(tx => tx.chain?.toLowerCase() === 'xch' && tx.direction === 'in')
    .reduce((sum, tx) => sum + Number(tx.amount), 0);

  const totalFIL = transactions
    .filter(tx => tx.chain?.toLowerCase() === 'fil' && tx.direction === 'in')
    .reduce((sum, tx) => sum + Number(tx.amount), 0);

  const txExplorerUrl = (tx: Transaction) => {
    const chain = tx.chain?.toLowerCase();
    if (!tx.tx_hash) return undefined;
    if (chain === 'xch') return `https://xch.dwd.com/info/coin/${tx.tx_hash}`;
    if (chain === 'fil') return `https://filscan.io/en/message/${tx.tx_hash}`;
    if (chain === 'ar') return `https://viewblock.io/arweave/tx/${tx.tx_hash}`;
    // fallback (np. bsc):
    return `https://bscscan.com/tx/${tx.tx_hash}`;
  };

  return (
    <div className="space-y-1 bg-black">
      {hasNoECYAnywhere && (
        <div className="relative overflow-hidden bg-black backdrop-blur-md border border-amber-400/30 rounded-xl p-4">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-900/10 to-transparent animate-pulse"></div>
          <div className="relative flex items-start space-x-3">
            <div className="flex-shrink-0 animate-bounce">
              <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <p className="text-amber-300 font-bold text-lg">No ECY Tokens Detected</p>
              <p className="text-amber-200/90 text-sm mt-1">
                You need ECY tokens in your wallet or staked to access the platform.
                Please acquire ECY tokens first.
              </p>
            </div>
          </div>
        </div>
      )}

      {hasV1Stake && (
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-900/10 via-indigo-500/10 to-blue-500/10 backdrop-blur-md border border-blue-400/30 rounded-xl p-4">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/10 to-transparent animate-pulse"></div>
          <div className="relative flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-400 blur-xl opacity-50 animate-ping"></div>
                <svg className="relative w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div>
              <p className="text-blue-300 font-bold text-lg">Migration Required</p>
              <p className="text-blue-200/90 text-sm mt-1">
                Unstake from V1 contract to migrate to V2 and earn additional Filecoin rewards.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="relative bg-black backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/10 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-black-900/20 via-transparent to-blue-500/20 animate-gradient"></div>
        </div>

        {/* Header */}
        <div className="relative flex items-center justify-between mb-8 bg-black">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-blue-400 blur-xl opacity-50 animate-pulse"></div>
              <h3 className="relative text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400">
                Staking Dashboard
              </h3>
            </div>
            <div className="px-2 py-1 bg-green-500/20 border border-green-400/30 rounded-full">
              <p className="text-xs text-green-300 font-semibold">V2 ACTIVE</p>
            </div>
          </div>
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent ml-4"></div>
        </div>

        {/* Wallets guard */}
        {!bothWalletsPresent && (
          <div className="relative bg-gradient-to-r from-red-500/20 to-pink-500/20 backdrop-blur-md border border-red-400/30 rounded-lg p-4 mb-6 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 to-pink-500/5 animate-pulse"></div>
            <p className="relative text-red-200 text-sm font-medium">
              To use staking, you must set up your XCH, FIL and AR wallets{' '}
              <button
                onClick={onOpenWalletSetup}
                className="text-red-300 underline font-semibold hover:text-white transition-all duration-300 hover:no-underline"
              >
                Set up now
              </button>
            </p>
          </div>
        )}

        {/* TVL */}
        <div className="relative mb-6 p-4 bg-gradient-to-r from-green-500/20 to-green-400/20 rounded-xl border border-green-700/30 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-green-500/5 animate-pulse"></div>
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-purple-200 text-sm font-medium mb-1">Total Value Locked</p>
              <p className="text-3xl font-black text-white">{formatNumber(totalStaked)} ECY</p>
            </div>
<div className="w-16 h-16 bg-gradient-to-br from-green-400/30 to-green-400/30 rounded-2xl flex items-center justify-center animate-float">
  <img
    src="/ecoway.png"
    alt="EcoWay logo"
    className="w-8 h-8 rounded-full object-cover ring-2 ring-green-300/60 bg-white/5"
    loading="lazy"
    decoding="async"
    width={32}
    height={32}
  />
</div>

          </div>
        </div>

        {/* Wallet / Stakes summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="group relative bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-md rounded-xl p-5 border border-green-400/30 transition-all duration-300 hover:scale-105 hover:border-green-400/50 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-green-400/10 to-emerald-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400/30 to-emerald-400/30 rounded-xl flex items-center justify-center group-hover:animate-bounce">
🏦
                </div>
                <span className="text-green-200 text-sm font-bold uppercase tracking-wider">Wallet</span>
              </div>
              <p className="text-3xl font-black text-white mb-1">{formatNumber(tokenBalance)}</p>
              <p className="text-xs text-green-200/80 font-medium">Available ECY</p>
            </div>
          </div>

          <div className="group relative bg-gradient-to-br from-orange-500/20 to-amber-500/20 backdrop-blur-md rounded-xl p-5 border border-orange-400/30 transition-all duration-300 hover:scale-105 hover:border-orange-400/50 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-400/10 to-amber-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-400/30 to-amber-400/30 rounded-xl flex items-center justify-center group-hover:animate-bounce">
🕒
                </div>
                <span className="text-orange-200 text-sm font-bold uppercase tracking-wider">Staked V1</span>
              </div>
              <p className="text-3xl font-black text-white mb-1">{formatNumber(stakedV1)}</p>
              {cooldownV1 ? (
                <p className="text-xs text-orange-200/80 truncate animate-pulse" title={`Available after: ${cooldownV1}`}>
                  {cooldownV1.split(',')[0]}
                </p>
              ) : (
                <p className="text-xs text-orange-200/80 font-medium">Legacy Contract</p>
              )}
            </div>
          </div>

          <div className="group relative bg-gradient-to-br from-blue-500/20 to-indigo-500/20 backdrop-blur-md rounded-xl p-5 border border-blue-400/30 transition-all duration-300 hover:scale-105 hover:border-blue-400/50 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-indigo-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400/30 to-indigo-400/30 rounded-xl flex items-center justify-center group-hover:animate-bounce">
🔐
                </div>
                <span className="text-blue-200 text-sm font-bold uppercase tracking-wider">Staked V2</span>
              </div>
              <p className="text-3xl font-black text-white mb-1">{formatNumber(stakedV2)}</p>
              {cooldownV2 ? (
                <p className="text-xs text-blue-200/80 truncate animate-pulse" title={`Available after: ${cooldownV2}`}>
                  {cooldownV2.split(',')[0]}
                </p>
              ) : (
                <p className="text-xs text-blue-200/80 font-medium">Active Contract</p>
              )}
            </div>
          </div>
        </div>

        {/* =================== /NEW Transaction History =================== */}

        {/* Earnings Section (kept BELOW history, per request) */}

        {/* Amount input */}
        <div className="relative bg-gradient-to-br from-slate-900/50 to-black/50 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/10 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-transparent to-blue-500/5 animate-gradient"></div>
          <div className="relative">
            <label className="block text-sm font-bold text-gray-200 mb-4 uppercase tracking-wider">
              Enter Staking Amount
            </label>
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <input
                  type="text"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => onAmountChange(e.target.value)}
                  disabled={anyBusy}
                  className="w-full bg-white/5 backdrop-blur border-2 border-white/10 rounded-xl px-6 py-4
                           text-white placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-green-400/30
                           focus:border-green-400/50 disabled:opacity-50 disabled:cursor-not-allowed
                           text-2xl font-bold transition-all duration-300"
                />
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-bold">
                  ECY
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={onSetMaxWallet}
                  disabled={anyBusy}
                  className="group px-6 py-4 bg-gradient-to-r from-white/5 to-white/10 backdrop-blur hover:from-white/10 hover:to-white/20
                           text-white rounded-xl text-sm font-black transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                           border-2 border-white/10 hover:border-white/20 uppercase tracking-widest hover:scale-105 hover:shadow-lg hover:shadow-white/10"
                  title="Use maximum wallet balance"
                >
                  <span className="relative">
                    MAX
                    <span className="absolute inset-0 bg-gradient-to-r from-green-400 to-blue-400 opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300"></span>
                  </span>
                </button>
                {hasV1Stake && (
                  <button
                    onClick={onSetMaxV1}
                    disabled={anyBusy}
                    className="group px-6 py-4 bg-gradient-to-r from-white/5 to-white/10 backdrop-blur hover:from-white/10 hover:to-white/20
                             text-white rounded-xl text-sm font-black transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                             border-2 border-white/10 hover:border-white/20 uppercase tracking-widest hover:scale-105 hover:shadow-lg hover:shadow-white/10"
                    title="Use maximum V1 staked amount"
                  >
                    V1
                  </button>
                )}
                {stakedV2Wei.gt(ethers.constants.Zero) && (
                  <button
                    onClick={onSetMaxV2}
                    disabled={anyBusy}
                    className="group px-6 py-4 bg-gradient-to-r from-white/5 to-white/10 backdrop-blur hover:from-white/10 hover:to-white/20
                             text-white rounded-xl text-sm font-black transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                             border-2 border-white/10 hover:border-white/20 uppercase tracking-widest hover:scale-105 hover:shadow-lg hover:shadow-white/10"
                    title="Use maximum V2 staked amount"
                  >
                    V2
                  </button>
                )}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
              <div className="flex justify-between p-2 bg-white/5 rounded-lg">
                <span className="text-gray-400">Available:</span>
                <span className="text-green-300 font-bold">{formatNumber(tokenBalance)} ECY</span>
              </div>
              {hasV1Stake && (
                <div className="flex justify-between p-2 bg-white/5 rounded-lg">
                  <span className="text-gray-400">V1 Locked:</span>
                  <span className="text-orange-300 font-bold">{formatNumber(stakedV1)} ECY</span>
                </div>
              )}
              {stakedV2Wei.gt(ethers.constants.Zero) && (
                <div className="flex justify-between p-2 bg-white/5 rounded-lg">
                  <span className="text-gray-400">V2 Locked:</span>
                  <span className="text-blue-300 font-bold">{formatNumber(stakedV2)} ECY</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-4">
          <button
            onClick={onStakeV2}
            disabled={!bothWalletsPresent || anyBusy || !canStakeV2}
            title={hasV1Stake ? 'Please unstake from V1 first' : ''}
            className="group relative flex-1 min-w-[150px] bg-gradient-to-r from-green-500 via-emerald-500 to-green-500 bg-size-200 bg-pos-0
                     text-white px-8 py-4 rounded-2xl font-black hover:bg-pos-100
                     transition-all duration-500 transform hover:scale-[1.03] disabled:opacity-50 disabled:cursor-not-allowed
                     disabled:hover:scale-100 shadow-2xl shadow-green-500/40 hover:shadow-green-500/60 uppercase tracking-wider overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            {isStaking ? (
              <span className="relative flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Staking...
              </span>
            ) : (
              <span className="relative">Stake ECY Token</span>
            )}
          </button>

          {hasV1Stake ? (
            <button
              onClick={onUnstakeV1}
              disabled={!bothWalletsPresent || anyBusy || stakedV1Wei.isZero()}
              className="group relative flex-1 min-w-[150px] bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 bg-size-200 bg-pos-0
                       text-white px-8 py-4 rounded-2xl font-black hover:bg-pos-100
                       transition-all duration-500 transform hover:scale-[1.03] disabled:opacity-50 disabled:cursor-not-allowed
                       disabled:hover:scale-100 shadow-2xl shadow-orange-500/40 hover:shadow-orange-500/60 uppercase tracking-wider overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              {isUnstaking ? (
                <span className="relative flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Unstaking...
                </span>
              ) : (
                <span className="relative">Unstake from V1</span>
              )}
            </button>
          ) : stakedV2Wei.gt(ethers.constants.Zero) ? (
            <button
              onClick={onUnstakeV2}
              disabled={!bothWalletsPresent || anyBusy || stakedV2Wei.isZero()}
              className="group relative flex-1 min-w-[150px] bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 bg-size-200 bg-pos-0
                       text-white px-8 py-4 rounded-2xl font-black hover:bg-pos-100
                       transition-all duration-500 transform hover:scale-[1.03] disabled:opacity-50 disabled:cursor-not-allowed
                       disabled:hover:scale-100 shadow-2xl shadow-purple-500/40 hover:shadow-purple-500/60 uppercase tracking-wider overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              {isUnstaking ? (
                <span className="relative flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Unstaking...
                </span>
              ) : (
                <span className="relative">Unstake from V2</span>
              )}
            </button>
          ) : null}
        </div>

        {/* Migration tip */}
        {hasV1Stake && (
          <div className="mt-6 relative bg-gradient-to-r from-blue-500/20 to-indigo-500/20 backdrop-blur-md border border-blue-400/30 rounded-xl p-4 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 animate-pulse"></div>
            <div className="relative flex items-start space-x-2">
              <span className="text-2xl animate-bounce">💡</span>
              <p className="text-sm text-blue-200">
                <span className="font-bold text-blue-300">Migration Tip:</span> Unstake from V1 contract to move to V2
                and earn additional <span className="text-yellow-300 font-bold">Filecoin rewards</span>.
              </p>
            </div>
          </div>
        )}

        {/* Status */}
        {status && (
          <div className="mt-6 relative bg-gradient-to-r from-black/5 to-black/10 backdrop-blur-md rounded-xl p-4 border border-white/20 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-transparent to-blue-500/5 animate-gradient"></div>
            <p className="relative text-sm text-gray-200 font-mono flex items-center">
              <span className="inline-block w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
              {status}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StakingPanel;
